import { AfterViewInit, Directive, ElementRef, effect, inject, Input, OnChanges, OnDestroy, Renderer2, SimpleChanges } from '@angular/core';
import { SpeedReadingGameServiceService } from '@pages/games/game/services/game-specific/speed-reading-game-service.service';
import { getNumberPartFromStringWithUnits } from '@shared/utils/stringUtils';

interface BlindPosition {
  top: number;
  height: number;
}

@Directive({
  selector: '[msBlindsManager]',
  standalone: false,
})
export class BlindsManagerDirective implements AfterViewInit, OnChanges, OnDestroy {
  private readonly _elementRef = inject(ElementRef<HTMLElement>);
  private readonly _renderer = inject(Renderer2);
  private readonly _speedReadingGameService = inject(SpeedReadingGameServiceService);
  private _resizeObserver?: ResizeObserver;
  private _blindsContainer?: HTMLElement;
  private _titleElement?: HTMLElement;
  private _paragraphsElement?: HTMLElement;
  private _blinds: HTMLElement[] = [];
  private _updateTimer?: number;

  @Input() msBlindsManager: boolean = false;
  @Input() titleSelector = '.title';
  @Input() paragraphsSelector = '.paragraphs';
  @Input() isBook = false;
  @Input() blindSide: 'top' | 'bottom' = 'top';
  @Input() blindWidth = 100;
  @Input() blindBackgroundColor = '#fff';

  constructor() {
    // Отслеживаем изменения fontSize через effect
    effect(() => {
      const fontSize = this._speedReadingGameService.speedReadingFontSize();
      if (fontSize !== null && this.msBlindsManager) {
        this._scheduleUpdate();
      }
    });
  }

  ngAfterViewInit(): void {
    if (this.msBlindsManager) {
      this._initialize();
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['msBlindsManager']) {
      if (this.msBlindsManager) {
        requestAnimationFrame(() => this._initialize());
      } else {
        this._cleanup();
      }
    }
  }

  ngOnDestroy(): void {
    this._cleanup();
    if (this._blindsContainer?.parentNode) {
      this._renderer.removeChild(this._elementRef.nativeElement, this._blindsContainer);
    }
  }

  private _initialize(): void {
    if (!this.msBlindsManager) return;

    const container = this._elementRef.nativeElement;
    this._titleElement = container.querySelector(this.titleSelector) as HTMLElement;
    this._paragraphsElement = container.querySelector(this.paragraphsSelector) as HTMLElement;

    if (!this._titleElement || !this._paragraphsElement) {
      requestAnimationFrame(() => this._initialize());
      return;
    }

    // Создаем или находим контейнер для шторок
    this._blindsContainer = container.querySelector('.blinds') as HTMLElement;
    if (!this._blindsContainer) {
      this._blindsContainer = this._renderer.createElement('div');
      this._renderer.addClass(this._blindsContainer, 'blinds');
      this._renderer.setStyle(this._blindsContainer, 'position', 'absolute');
      this._renderer.setStyle(this._blindsContainer, 'top', '0');
      this._renderer.setStyle(this._blindsContainer, 'width', '100%');
      this._renderer.setStyle(this._blindsContainer, 'z-index', '1');
      this._renderer.insertBefore(container, this._blindsContainer, container.firstChild);
    }

    // Настраиваем ResizeObserver
    this._resizeObserver?.disconnect();
    this._resizeObserver = new ResizeObserver(() => this._scheduleUpdate());
    this._resizeObserver.observe(this._titleElement);
    this._resizeObserver.observe(this._paragraphsElement);

    this._updateBlinds();
  }

  private _cleanup(): void {
    this._resizeObserver?.disconnect();
    if (this._updateTimer) {
      cancelAnimationFrame(this._updateTimer);
    }
    if (this._blindsContainer) {
      this._blinds.forEach(blind => {
        if (blind.parentNode === this._blindsContainer) {
          this._renderer.removeChild(this._blindsContainer!, blind);
        }
      });
      this._blinds = [];
    }
  }

  private _scheduleUpdate(): void {
    if (this._updateTimer) {
      cancelAnimationFrame(this._updateTimer);
    }
    this._updateTimer = requestAnimationFrame(() => {
      this._updateBlinds();
      this._updateTimer = undefined;
    });
  }

  private _updateBlinds(): void {
    if (!this.msBlindsManager || !this._titleElement || !this._paragraphsElement || !this._blindsContainer) {
      return;
    }

    if (this._titleElement.offsetHeight === 0 && this._paragraphsElement.offsetHeight === 0) {
      return;
    }

    // Удаляем старые шторки
    this._blinds.forEach(blind => {
      if (blind.parentNode === this._blindsContainer) {
        this._renderer.removeChild(this._blindsContainer!, blind);
      }
    });
    this._blinds = [];

    // Создаем DocumentFragment для батчинга операций
    const fragment = document.createDocumentFragment();

    // Создаем шторки для заголовка
    const titleBlinds = this._calculateBlindsPositions(this._titleElement, true);
    titleBlinds.forEach(blind => {
      fragment.appendChild(this._createBlindElement(blind, true));
    });

    // Создаем шторки для параграфов
    const paragraphBlinds = this._calculateBlindsPositions(this._paragraphsElement, false);
    paragraphBlinds.forEach(blind => {
      fragment.appendChild(this._createBlindElement(blind, false));
    });

    // Добавляем все шторки одним батчем
    this._renderer.appendChild(this._blindsContainer, fragment);
    
    // Сохраняем ссылки на созданные элементы
    this._blinds = Array.from(this._blindsContainer.querySelectorAll('._blind')) as HTMLElement[];
  }

  private _calculateBlindsPositions(element: HTMLElement, isTitle: boolean): BlindPosition[] {
    if (element.offsetHeight === 0) return [];

    const containerHeight = element.offsetHeight;
    const computedStyle = window.getComputedStyle(element);
    const lineHeightValue = computedStyle.getPropertyValue('line-height');
    const fontSizeValue = computedStyle.getPropertyValue('font-size');

    let lineHeight = getNumberPartFromStringWithUnits(lineHeightValue, 'px');
    if (lineHeightValue === 'normal' || lineHeight <= 0 || isNaN(lineHeight)) {
      const fontSize = getNumberPartFromStringWithUnits(fontSizeValue, 'px');
      lineHeight = fontSize * 1.5;
    }

    let linesCount = Math.round(containerHeight / lineHeight);
    if (linesCount >= 3 && this.isBook && !isTitle) {
      linesCount -= 2;
    }

    const offsetTop = element.offsetTop;
    const positions: BlindPosition[] = [];
    for (let i = 0; i < linesCount; i++) {
      positions.push({
        top: offsetTop + (i * lineHeight),
        height: lineHeight
      });
    }

    return positions;
  }

  private _createBlindElement(position: BlindPosition, isTitle: boolean): HTMLElement {
    const blindElement = this._renderer.createElement('div');
    this._renderer.addClass(blindElement, '_blind');
    if (isTitle) {
      this._renderer.addClass(blindElement, '_blind__title');
    }
    if (this.blindSide === 'bottom') {
      this._renderer.addClass(blindElement, '_blind__bottom');
    }

    // Устанавливаем стили одной операцией через setAttribute для производительности
    blindElement.style.cssText = `position: absolute; left: 0; right: 0; width: 100%; top: ${position.top}px; height: ${position.height}px;`;

    // Создаем внутренний элемент шторки
    const innerBlind = this._renderer.createElement('div');
    this._renderer.addClass(innerBlind, 'blind');
    innerBlind.style.cssText = `height: ${this.blindWidth}%; background: ${this.blindBackgroundColor};`;
    this._renderer.appendChild(blindElement, innerBlind);

    return blindElement;
  }
}
